import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabaseClient';
import { fetchOrderById, fetchOrdersByVenue } from '../utils/dataService';

const OrdersContext = createContext();

const removeOrderFromList = (list, orderId) => list.filter((order) => order.id !== orderId);

const getOrderSortValue = (order) => {
  const createdAt = order?.created_at || order?.createdAt;
  const timestamp = createdAt ? new Date(createdAt).getTime() : 0;
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const sortOrdersNewestFirst = (orders) => [...orders].sort((left, right) => {
  const timeDelta = getOrderSortValue(right) - getOrderSortValue(left);
  if (timeDelta !== 0) {
    return timeDelta;
  }

  return (right?.id || 0) - (left?.id || 0);
});

const upsertOrderInList = (list, order, options = {}) => {
  const { prepend = false } = options;
  const existingIndex = list.findIndex((currentOrder) => currentOrder.id === order.id);
  const filtered = removeOrderFromList(list, order.id);

  if (existingIndex !== -1) {
    filtered.splice(existingIndex, 0, order);
    return filtered;
  }

  return prepend ? [order, ...filtered] : [...filtered, order];
};

export const OrdersProvider = ({ children, venueId }) => {
  const [pendingOrders, setPendingOrders] = useState([]);
  const [preparingOrders, setPreparingOrders] = useState([]);
  const [completedOrders, setCompletedOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const allOrders = useMemo(() => [
    ...pendingOrders,
    ...preparingOrders,
    ...completedOrders,
  ], [pendingOrders, preparingOrders, completedOrders]);

  const categorizeOrders = useCallback((orders) => {
    const pendingOrdersList = [];
    const preparingOrdersList = [];
    const completedOrdersList = [];

    orders.forEach((order) => {
      if (order.payment_status === 'paid' || order.status === 'deleted') {
        return;
      }

      switch (order.status) {
        case 'pending':
          pendingOrdersList.push(order);
          break;
        case 'preparing':
          preparingOrdersList.push(order);
          break;
        case 'completed':
          completedOrdersList.push(order);
          break;
        default:
          break;
      }
    });

    setPendingOrders(pendingOrdersList);
    setPreparingOrders(preparingOrdersList);
    setCompletedOrders(sortOrdersNewestFirst(completedOrdersList));
  }, []);

  const refreshOrders = useCallback(async () => {
    const fetchedOrders = await fetchOrdersByVenue(venueId);
    categorizeOrders(fetchedOrders);
  }, [categorizeOrders, venueId]);

  const removeOrder = useCallback((orderId) => {
    setPendingOrders((prev) => removeOrderFromList(prev, orderId));
    setPreparingOrders((prev) => removeOrderFromList(prev, orderId));
    setCompletedOrders((prev) => removeOrderFromList(prev, orderId));
  }, []);

  const reconcileOrder = useCallback((order, options = {}) => {
    const { prependCompleted = false } = options;

    if (!order) {
      return;
    }

    if (order.payment_status === 'paid' || order.status === 'deleted') {
      removeOrder(order.id);
      return;
    }

    setPendingOrders((prev) =>
      order.status === 'pending'
        ? upsertOrderInList(prev, order)
        : removeOrderFromList(prev, order.id)
    );
    setPreparingOrders((prev) =>
      order.status === 'preparing'
        ? upsertOrderInList(prev, order)
        : removeOrderFromList(prev, order.id)
    );
    setCompletedOrders((prev) =>
      order.status === 'completed'
        ? upsertOrderInList(prev, order, { prepend: prependCompleted })
        : removeOrderFromList(prev, order.id)
    );
  }, [removeOrder]);

  const fetchAndReconcileOrder = useCallback(async (orderId, options = {}) => {
    if (!orderId) {
      return;
    }

    const order = await fetchOrderById(orderId);
    if (!order) {
      return;
    }

    if (order.tables.venue_id !== venueId) {
      return;
    }

    reconcileOrder(order, options);
  }, [reconcileOrder, venueId]);

  const handleOrderChange = useCallback(async (payload) => {
    if (payload.eventType === 'DELETE') {
      removeOrder(payload.old.id);
      return;
    }

    const orderId = payload.new ? payload.new.id : payload.old.id;
    await fetchAndReconcileOrder(orderId, {
      prependCompleted: payload.eventType === 'UPDATE' || payload.eventType === 'INSERT',
    });
  }, [fetchAndReconcileOrder, removeOrder]);

  const handleOrderItemChange = useCallback(async (payload) => {
    if (payload.eventType === 'DELETE') {
      await refreshOrders();
      return;
    }

    const orderId = payload.new ? payload.new.order_id : payload.old.order_id;
    await fetchAndReconcileOrder(orderId);
  }, [fetchAndReconcileOrder, refreshOrders]);

  useEffect(() => {
    const initializeOrders = async () => {
      await refreshOrders();
      setLoading(false);
    };

    initializeOrders();

    const ordersChannel = supabase
      .channel('public:orders')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders' }, handleOrderChange)
      .subscribe();

    const orderItemsChannel = supabase
      .channel('public:order_items')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'order_items' }, handleOrderItemChange)
      .subscribe();

    return () => {
      supabase.removeChannel(ordersChannel);
      supabase.removeChannel(orderItemsChannel);
    };
  }, [handleOrderChange, handleOrderItemChange, refreshOrders]);

  return (
    <OrdersContext.Provider
      value={{
        pendingOrders,
        preparingOrders,
        completedOrders,
        setPendingOrders,
        setPreparingOrders,
        setCompletedOrders,
        loading,
        allOrders,
        refreshOrders,
      }}
    >
      {children}
    </OrdersContext.Provider>
  );
};

export const useOrdersContext = () => useContext(OrdersContext);
